'use client'

import { useTranslation } from 'next-i18next'
import { useCallback, useMemo } from 'react'

import { usePdpColor } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { useLayoutContext } from '@src/components/pl-page-context'
import { usePlPdpProduct } from '@src/components/widgets/use-ia-impression'
import type { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import { isProductBeauty, isProductVitamin } from '@src/quince-site-common/src/helpers/productHelper'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { productContextInitialState } from '@src/quince-site-pdp/src/contexts/ProductPageContext'
import { RECOMMENDED_SECTION_IDS } from '@src/quince-site-pdp/src/contexts/productPageContext.constants'
import { getTrackingMetaData as getFrequentlyBoughtTogetherTrackingMetaData } from '@src/quince-site-pdp/src/organisms/productPageSections/frequentlyBoughtTogether/FrequentlyBoughtTogether.constants'
import {
  getTrackingMetaData as getCompleteTheLookTrackingMetaData,
  TRACKING_METADATA as COMPLETE_THE_LOOK_TRACKING,
} from '@src/quince-site-pdp/src/organisms/productPageSections/pairWithProducts/pairWithProducts.helpers'
import { getTrackingMetaData as getSimilarProductsTrackingMetaData } from '@src/quince-site-pdp/src/organisms/productPageSections/similarProducts/SimilarProducts.constants'
import { FINDING_METHOD_AGGREGATE } from '@src/telemetry/findingMethod'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackInternalEvent } from '@src/telemetry/track'

import { useCardImpressionTracking } from './single-product-card/card-tracking-context'

import type { RecoCardTrackingMetaData } from './single-product-card/card-tracking-context'
import type { MutableRefObject } from 'react'

// `useTrackIsElementVisible(getter, cb)` is used ONLY for scroll-into-view detection here. Its own
// IS_VISIBLE fire goes through the shared `trackEvent` (all integrations, incl. Mixpanel) — which we do
// NOT want (these reco impressions are InternalAnalytics-only). So the getter is a stable no-op and the
// callback fires every event via `trackInternalEvent` (IA-only). Module-level keeps the getter's
// reference stable across renders (the hook lists it in a dep array).
const NO_HOOK_IS_VISIBLE = () => undefined

// Telemetry action ids (session-stream `entity_id`) — stable identifiers, not user-facing copy. Held as
// module-level consts (not inline literals) so they read as tokens and never trip i18next/no-literal-string.
const ACTION_COMPLETE_THE_LOOK = 'complete_the_look'
const ACTION_PDP_SIMILAR_PRODUCT = 'pdp_similar_product'
const ACTION_FREQUENTLY_PURCHASED_TOGETHER = 'frequently_purchased_together'
const RECO_MODULE_CTL = 'CTL'
// PRODUCT_CLICK category/action ids per band — mirror the non-PL reco DS blocks' `productClickTrackConfig`
// (`similar-products-ds` / `frequently-bought-together-ds`; CTL reuses `pairWithProducts` TRACKING_METADATA).
// Stable session-stream tokens, not user-facing copy → module-level to satisfy i18next/no-literal-string.
const CATEGORY_SIMILAR_PRODUCTS = 'similar_products'
const ACTION_CLICK_SIMILAR_PRODUCT = 'click_similar_product'
const CATEGORY_FREQUENTLY_PURCHASED_TOGETHER = 'frequently_purchased_together'
const ACTION_CLICK_FREQUENTLY_PURCHASED_PRODUCT = 'click_frequently_purchased_product'
const COMPLETE_ROUTINE_TITLE = 'Complete your routine'
const RECO_SLOTS = productContextInitialState.recommendationUIData
// Canonical CTL display title = the non-PL SLOT1 module_title ("Complete the Look"), sourced from the same
// slot definitions as MTE/FBT below rather than hard-coded, so casing/wording can't drift from non-PL.
const COMPLETE_THE_LOOK_TITLE = RECO_SLOTS[RECOMMENDED_SECTION_IDS.SLOT1].module_title
// Canonical module_title_key per reco slot (single source of truth = the non-PL slot definitions). Used
// both to recognize a band by its localized heading AND to derive its tracked display name via `t(key)`.
const MTE_MODULE_TITLE_KEY = RECO_SLOTS[RECOMMENDED_SECTION_IDS.SLOT2].module_title_key
const FBT_MODULE_TITLE_KEY = RECO_SLOTS[RECOMMENDED_SECTION_IDS.SLOT3].module_title_key
const STL_MODULE_TITLE_KEYS = ['shop_the_look', 'complete_your_routine']
const MTE_MODULE_TITLE_KEYS = [MTE_MODULE_TITLE_KEY]
const FBT_MODULE_TITLE_KEYS = [FBT_MODULE_TITLE_KEY]
const CAROUSEL_DIRECTION_PREV = 'prev'
const CAROUSEL_DIRECTION_NEXT = 'next'

interface RecoBandTelemetryParams {
  /**
   * Shop-the-Look band (stl* pl_widget_ids) → non-PL `pair-with-products-ds` (`complete_the_look`).
   * Primary identity is the BE-driven `pl_widget_ids` Statsig match; the hook ALSO falls back to a
   * localized-title match (see STL_MODULE_TITLE_KEYS / `isStlBand`) so tracking mirrors non-PL even when
   * the stl config id isn't set.
   */
  isShopTheLookBand: boolean
  /**
   * More-to-Explore band (mte* pl_widget_ids) → non-PL `similar-products-ds` (`pdp_similar_product`).
   * Primary identity is the BE-driven `pl_widget_ids` Statsig match; the hook ALSO falls back to a
   * localized-title match (see MTE_MODULE_TITLE_KEYS / `isMteBand`) so tracking mirrors non-PL even when
   * the mte config id isn't set.
   */
  isMoreToExploreBand: boolean
  /**
   * "Quince Shoppers Also Love" / FBT band (fbt* pl_widget_ids) → non-PL `frequently-bought-together`
   * (`frequently_purchased_together`). Primary identity is the BE-driven `pl_widget_ids` Statsig match;
   * the hook ALSO falls back to a localized-title match (see FBT_MODULE_TITLE_KEYS / `isFbtBand`) so
   * tracking mirrors non-PL even when the fbt config id isn't set. All three flags drive only tracking here.
   */
  isFrequentlyPurchasedTogetherBand: boolean
  /** The band's data-driven title (used for the impression's contentContainerDisplayName + reco payload). */
  bandTitle?: string
  /** Widget's API-request key (recommendationView payload context). */
  widgetId?: string
  /** Post-displayLimit rendered card count (recommendationView payload context). */
  productCount: number
}

/**
 * Arrow-click directions consumed by {@link useRecoBandTelemetry}'s `onCarouselArrowClick`.
 */
export const CAROUSEL_ARROW_DIRECTION = {
  PREV: CAROUSEL_DIRECTION_PREV,
  NEXT: CAROUSEL_DIRECTION_NEXT,
} as const

/**
 * Shared reco-band telemetry for the PDP PL reco containers (HORIZONTAL_PRODUCT_LIST +
 * PRODUCT_GRID_CAROUSEL). Both containers reuse this ONE hook so their reco telemetry stays in lockstep,
 * and every event is emitted InternalAnalytics-only via `trackInternalEvent` (no Mixpanel/GA fan-out),
 * mirroring the non-PL reco DS blocks (`pair-with-products` / `similar-products`).
 *
 * Returns:
 * - `ref` — attach to the band's outer container. On scroll-into-view (guarded once by the hook's
 *   `seenRef`) it fires a per-band `IS_VISIBLE` for a MAPPED band (Shop-the-Look → `complete_the_look`,
 *   More-to-Explore → `pdp_similar_product`, Quince-Shoppers-Also-Love/FBT →
 *   `frequently_purchased_together`; unmapped bands like "You may also like" emit none) +
 *   `recommendationView` on EVERY band.
 * - `onCarouselArrowClick(direction)` — wire to the carousel arrows to fire `CAROUSEL_LEFT_CLICK` (prev)
 *   / `CAROUSEL_RIGHT_CLICK` (next). The IA handler reads only the auto-generated `eventId`;
 *   `module_name`/`module_id` (band title + widget id) mirror the non-PL payload shape.
 */
export function useRecoBandTelemetry({
  isShopTheLookBand,
  isMoreToExploreBand,
  isFrequentlyPurchasedTogetherBand,
  bandTitle,
  widgetId,
  productCount,
}: RecoBandTelemetryParams): {
  ref: MutableRefObject<HTMLDivElement | null>
  onCarouselArrowClick: (direction: string) => void
  /**
   * Per-band batched `BULK_PRODUCT_CARD_VIEW` tracker key for this band's `SingleProductCard` leaves.
   * Publish it via `CardTrackingProvider` so each band's card impressions carry that band's
   * `trackingId` (+ the non-PL reco context: `findingMethod` / `source_product_*` / `reco_module`)
   * instead of the flattened page-level `pdp-product-display`. `undefined` for unmapped bands.
   */
  cardProductViewTracker?: string
  /**
   * Per-band `PRODUCT_CLICK` config for this band's `SingleProductCard` leaves. Publish via
   * `CardTrackingProvider` so each band's card clicks carry that band's non-PL click telemetry
   * (category/action + `source_product_*` / `findingMethod` / `reco_module`). `undefined` for unmapped
   * bands → the leaf renders with no click tracking.
   */
  cardClickTrackConfig?: TrackConfig
} {
  const productId = useLayoutContext()?.pdp?.productIds?.productId
  const selectedColor = usePdpColor()
  // Product classification for the vitamin/beauty display-title override below (memoized projection of
  // the buy-box widget payload; `undefined` off the PDP, where no band maps anyway).
  const plProduct = usePlPdpProduct()
  const { t } = useTranslation('common')

  const normalizedBandTitle = (bandTitle ?? '').trim().toLowerCase()
  const matchesModuleTitle = (keys: string[]) =>
    normalizedBandTitle.length > 0 && keys.some(key => t(key).trim().toLowerCase() === normalizedBandTitle)
  const isStlBand = isShopTheLookBand || matchesModuleTitle(STL_MODULE_TITLE_KEYS)
  const isMteBand = isMoreToExploreBand || matchesModuleTitle(MTE_MODULE_TITLE_KEYS)
  const isFbtBand = isFrequentlyPurchasedTogetherBand || matchesModuleTitle(FBT_MODULE_TITLE_KEYS)

  // Tracked display name = the STATIC canonical `module_title` (non-PL parity): similar-products-ds and
  // frequently-bought-together-ds track `module_title` — a fixed English literal — and only RENDER the
  // localized heading via `t(module_title_key)`. Using the static value keeps the tracked name
  // locale-invariant (e.g. fr-ca still sends "More to explore", matching non-PL), whereas `t(key)` would
  // send "Plus à découvrir". Band IDENTITY still matches the localized rendered heading via
  // `matchesModuleTitle(t(key))` above — only the tracked display name is the static string.
  const mteDisplayTitle = RECO_SLOTS[RECOMMENDED_SECTION_IDS.SLOT2].module_title
  const fbtDisplayTitle = RECO_SLOTS[RECOMMENDED_SECTION_IDS.SLOT3].module_title

  // Per-band IS_VISIBLE payload (or undefined for unmapped bands). Context/trackingId mirror each
  // non-PL section's payload: both carry `productId` + `selectedColor`; `complete_the_look` also carries
  // findingMethod; `pdp_similar_product` does not. The metadata spread comes FIRST so the canonical
  // `productId`/`selectedColor`/`findingMethod` keys can never be silently shadowed if a helper ever
  // grows overlapping fields.
  const getBandImpressionParams = useCallback(() => {
    if (isStlBand) {
      const displayTitle =
        isProductVitamin(plProduct) || isProductBeauty(plProduct) ? COMPLETE_ROUTINE_TITLE : COMPLETE_THE_LOOK_TITLE
      return {
        action: ACTION_COMPLETE_THE_LOOK,
        value: true,
        context: {
          ...getCompleteTheLookTrackingMetaData(displayTitle).context,
          productId,
          selectedColor,
          findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
        },
      }
    }
    if (isMteBand) {
      return {
        action: ACTION_PDP_SIMILAR_PRODUCT,
        value: true,
        context: {
          ...getSimilarProductsTrackingMetaData(mteDisplayTitle).context,
          productId,
          selectedColor,
        },
      }
    }
    if (isFbtBand) {
      // Non-PL `frequently_purchased_together` payload: `{ productId, selectedColor, ...trackingMetaData }`
      return {
        action: ACTION_FREQUENTLY_PURCHASED_TOGETHER,
        value: true,
        context: {
          ...getFrequentlyBoughtTogetherTrackingMetaData(fbtDisplayTitle).context,
          productId,
          selectedColor,
        },
      }
    }
    return undefined
  }, [isStlBand, isMteBand, isFbtBand, productId, selectedColor, mteDisplayTitle, fbtDisplayTitle, plProduct])

  // Fires once on scroll-into-view: the per-band IS_VISIBLE impression (when mapped) +
  // `recommendationView` — both InternalAnalytics-only.
  const trackBandVisible = useCallback(() => {
    const impression = getBandImpressionParams()
    if (impression) trackInternalEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, impression)
    trackInternalEvent(TELEMETRY_EVENT_NAMES.RECOMMENDATION_VIEW, { title: bandTitle, widgetId, productCount })
  }, [getBandImpressionParams, bandTitle, widgetId, productCount])

  const { ref } = useTrackIsElementVisible(NO_HOOK_IS_VISIBLE, trackBandVisible)

  // Per-band card-impression metadata for BULK_PRODUCT_CARD_VIEW — a DISTINCT payload from the
  // IS_VISIBLE band impression above. Mirrors each non-PL reco section's batched-analytics flush:
  // Complete-the-Look adds `reco_module: 'CTL'` (`pair-with-products-legacy`); More-to-Explore and
  // "Quince Shoppers Also Love"/FBT add `source_product_id` (PDP product spid) + `source_product_color`
  // (selected color) + `findingMethod: Recommendation` (`similar-products` / `frequently-bought-together`
  // via `productClickTrackConfig.context`). Unmapped bands (e.g. "You may also like"/BOYT, whose
  // container owns its own metadata) get `undefined` → no per-band override.
  const cardTrackingMetaData = useMemo<RecoCardTrackingMetaData | undefined>(() => {
    if (isStlBand) {
      // Tracked display name = "Complete the Look" (non-PL module_title), not the "Shop the Look" heading.
      const displayTitle =
        isProductVitamin(plProduct) || isProductBeauty(plProduct) ? COMPLETE_ROUTINE_TITLE : COMPLETE_THE_LOOK_TITLE
      return {
        context: {
          ...getCompleteTheLookTrackingMetaData(displayTitle).context,
          reco_module: RECO_MODULE_CTL,
        },
      }
    }
    if (isMteBand) {
      // Localized canonical module_title (`t('more_to_explore')`), matching non-PL similar-products'
      // tracked `module_title` — not the raw widget `data.title` (whose casing can differ).
      return {
        context: {
          ...getSimilarProductsTrackingMetaData(mteDisplayTitle).context,
          source_product_id: plProduct?.spid,
          source_product_color: selectedColor,
          findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
        },
      }
    }
    if (isFbtBand) {
      // Localized canonical module_title (`t('quince_shoppers_also_love')`), matching non-PL FBT.
      return {
        context: {
          ...getFrequentlyBoughtTogetherTrackingMetaData(fbtDisplayTitle).context,
          source_product_id: plProduct?.spid,
          source_product_color: selectedColor,
          findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
        },
      }
    }
    return undefined
  }, [isStlBand, isMteBand, isFbtBand, mteDisplayTitle, fbtDisplayTitle, plProduct, selectedColor])

  // Registers the per-band batched stream + returns its tracker key (base page tracker + band
  // trackingId). The band's leaves push into this key via `CardTrackingProvider`.
  const cardProductViewTracker = useCardImpressionTracking(cardTrackingMetaData)

  // Per-band PRODUCT_CLICK config for this band's `SingleProductCard` leaves — mirrors each non-PL reco
  // DS block's `productClickTrackConfig` exactly (`pair-with-products-ds` / `similar-products-ds` /
  // `frequently-bought-together-ds`), so a PL reco card click carries the same category/action + reco
  // context (`source_product_*` / `isRecommendationProduct` / `findingMethod` / `reco_module`) as non-PL.
  // Published via `CardTrackingProvider`; `undefined` for unmapped bands → no click tracking. CTL omits
  // `trackInternal` and adds `id`/`reco_module` (pair-with parity); MTE/FBT set `trackInternal: true`.
  const cardClickTrackConfig = useMemo<TrackConfig | undefined>(() => {
    if (isStlBand) {
      const displayTitle =
        isProductVitamin(plProduct) || isProductBeauty(plProduct) ? COMPLETE_ROUTINE_TITLE : COMPLETE_THE_LOOK_TITLE
      return {
        category: COMPLETE_THE_LOOK_TRACKING.category,
        action: COMPLETE_THE_LOOK_TRACKING.action,
        context: {
          // Product-specific id so the batched bulk-view subscription key can't collide across PDPs
          // after a client-side product navigation (pair-with keyed the bulk tracker by product.slug).
          id: plProduct?.slug,
          source_product_id: plProduct?.spid,
          source_product_color: selectedColor,
          isRecommendationProduct: true,
          ...getCompleteTheLookTrackingMetaData(displayTitle).context,
          findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
          reco_module: RECO_MODULE_CTL,
        },
      }
    }
    if (isMteBand) {
      return {
        trackInternal: true,
        category: CATEGORY_SIMILAR_PRODUCTS,
        action: ACTION_CLICK_SIMILAR_PRODUCT,
        context: {
          source_product_id: plProduct?.spid,
          source_product_color: selectedColor,
          isRecommendationProduct: true,
          ...getSimilarProductsTrackingMetaData(mteDisplayTitle).context,
          findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
        },
      }
    }
    if (isFbtBand) {
      return {
        trackInternal: true,
        category: CATEGORY_FREQUENTLY_PURCHASED_TOGETHER,
        action: ACTION_CLICK_FREQUENTLY_PURCHASED_PRODUCT,
        context: {
          source_product_id: plProduct?.spid,
          source_product_color: selectedColor,
          isRecommendationProduct: true,
          ...getFrequentlyBoughtTogetherTrackingMetaData(fbtDisplayTitle).context,
          findingMethod: FINDING_METHOD_AGGREGATE.RECOMMENDATION,
        },
      }
    }
    return undefined
  }, [isStlBand, isMteBand, isFbtBand, plProduct, selectedColor, mteDisplayTitle, fbtDisplayTitle])

  // Carousel arrow navigation → CAROUSEL_LEFT_CLICK (prev) / CAROUSEL_RIGHT_CLICK (next), IA-only.
  const onCarouselArrowClick = useCallback(
    (direction: string) => {
      const event =
        direction === CAROUSEL_ARROW_DIRECTION.PREV
          ? TELEMETRY_EVENT_NAMES.CAROUSEL_LEFT_CLICK
          : TELEMETRY_EVENT_NAMES.CAROUSEL_RIGHT_CLICK
      trackInternalEvent(event, { module_name: bandTitle ?? '', module_id: widgetId ?? '' })
    },
    [bandTitle, widgetId],
  )

  return { ref, onCarouselArrowClick, cardProductViewTracker, cardClickTrackConfig }
}
